import type { LayoutChangeEvent, ViewProps } from "@-/com.npmjs.react-native";
import { type ReactNode, useContext, useLayoutEffect, useState, useSyncExternalStore } from "react";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text, TextClassContext } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import { Slot } from "@rn-primitives/slot";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { SurfaceHeaderRowContext, type createSurfaceHeaderRow } from "#surface-header-row";

/**
 * Tight bordered card. Composes with `SurfaceHeader`, `SurfaceContent`, and `SurfaceFooter` to
 * produce a `bg-surface` body with an off-white (`bg-surface-header`) header strip, or a white
 * one (`<SurfaceHeader background="surface">`).
 *
 * Outer chrome (border, rounding, overflow clipping) lives here so children don't have to spell
 * their own edges — `SurfaceHeader` contributes its background, both header/footer contribute
 * dividers, and `SurfaceContent` is unpadded by default for tight grid layouts.
 */
export function Surface({ className, ...props }: ViewProps & React.RefAttributes<View>) {
  return (
    <TextClassContext value="text-surface-foreground">
      <View
        className={cn(
          "flex flex-col overflow-hidden rounded-xs border border-border/60 bg-surface",
          className,
        )}
        {...props}
      />
    </TextClassContext>
  );
}

/**
 * The header strip's fill: `"header"` (default) is the off-white strip, and `"surface"` keeps
 * the body's own white so header content stays maximally visible (the divider still separates them).
 */
type SurfaceHeaderBackground = "header" | "surface";

function surfaceHeaderClassName(background: SurfaceHeaderBackground) {
  return cn(
    "border-b border-border/60 px-3",
    background === "surface" ? "bg-surface" : "bg-surface-header",
  );
}

export function SurfaceHeader({
  asChild,
  background = "header",
  className,
  ...props
}: ViewProps &
  React.RefAttributes<View> & { asChild?: boolean; background?: SurfaceHeaderBackground }) {
  const alignment = useContext(SurfaceHeaderRowContext);
  if (alignment !== null) {
    return (
      <AlignedSurfaceHeader
        asChild={asChild}
        background={background}
        className={className}
        key={`${alignment.itemKey}:${alignment.row.width}`}
        {...alignment}
        {...props}
      />
    );
  }
  const Component = asChild ? Slot : View;
  return <Component className={cn(surfaceHeaderClassName(background), className)} {...props} />;
}

/**
 * A header that shows and hides its surface's body when pressed, with a chevron on its right: up
 * while the body shows, down while it is hidden. The caller renders the body only while
 * `isExpanded`; a collapsed surface is just this header, so it drops the divider below it.
 */
export function SurfaceCollapsibleHeader({
  accessibilityLabel,
  children,
  className,
  isExpanded,
  onExpandedChange,
  testID,
}: {
  /** What a screen reader calls the toggle, usually the surface's title. */
  accessibilityLabel: string;
  children: ReactNode;
  /** The row of header content to the left of the chevron. */
  className?: string;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  testID?: string;
}) {
  return (
    <SurfaceHeader className={cn("px-0", !isExpanded && "border-b-0")}>
      <Pressable
        accessibilityHint={isExpanded ? "Hides this section" : "Shows this section"}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button"
        accessibilityState={{ expanded: isExpanded }}
        className="flex-row items-center gap-2 px-3 active:bg-accent"
        onPress={() => onExpandedChange(!isExpanded)}
        testID={testID}
      >
        <View className={cn("min-w-0 flex-1", className)}>{children}</View>
        <Icon
          className="text-muted-foreground"
          name={isExpanded ? "LuChevronUp" : "LuChevronDown"}
          size={16}
        />
      </Pressable>
    </SurfaceHeader>
  );
}

function AlignedSurfaceHeader({
  asChild,
  background,
  className,
  itemKey,
  onLayout,
  row,
  style,
  ...props
}: ViewProps &
  React.RefAttributes<View> & {
    asChild?: boolean;
    background: SurfaceHeaderBackground;
    itemKey: string;
    row: ReturnType<typeof createSurfaceHeaderRow>;
  }) {
  const rowHeight = useSyncExternalStore(row.subscribe, row.getHeight, row.getHeight);
  const [naturalHeight, setNaturalHeight] = useState(0);
  // Measure without the added padding, so wrapping and font/viewport changes can shrink a row too.
  const paddingBottom = naturalHeight > 0 ? Math.max(0, rowHeight - naturalHeight) : 0;
  const handleLayout = (event: LayoutChangeEvent) => {
    const height = Math.round((event.nativeEvent.layout.height - paddingBottom) * 1000) / 1000;
    setNaturalHeight(height);
    onLayout?.(event);
  };
  // LegendList can recycle a header into another row without changing its natural height.
  useLayoutEffect(() => {
    if (naturalHeight > 0) {
      row.setHeight(itemKey, naturalHeight);
    }
  }, [itemKey, naturalHeight, row]);
  const Component = asChild ? Slot : View;
  return (
    <Component
      className={cn(surfaceHeaderClassName(background), className)}
      {...props}
      onLayout={handleLayout}
      style={[style, { paddingBottom }]}
    />
  );
}

export function SurfaceTitle({
  className,
  ...props
}: React.ComponentProps<typeof Text> & React.RefAttributes<Text>) {
  return (
    <Text
      aria-level={3}
      className={cn("pt-2 text-sm font-semibold", className)}
      role="heading"
      {...props}
    />
  );
}

export function SurfaceDescription({
  className,
  ...props
}: React.ComponentProps<typeof Text> & React.RefAttributes<Text>) {
  return (
    <Text className={cn("pb-2 text-xs capitalize", className)} colorVariant="muted" {...props} />
  );
}

export function SurfaceContent({ className, ...props }: ViewProps & React.RefAttributes<View>) {
  return <View className={cn(className)} {...props} />;
}

export function SurfaceFooter({ className, ...props }: ViewProps & React.RefAttributes<View>) {
  return (
    <View
      className={cn("flex flex-row items-center border-t border-border/60 px-3 py-2", className)}
      {...props}
    />
  );
}
